import { Row } from '@tanstack/react-table';
import { useFormContext, useWatch } from 'react-hook-form';

import { formatTrimmedDecimal, parseFormNumber, roundToPrecision } from '@/helpers/finance';
import { FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES } from '@/modules/finance/common/constants/rounding.constants';

import { GenerateBillListItem, GenerateBillMemberOverheadListItem } from '../../../types';
import { getBillFieldName } from '../bill-row.helpers';

/** `hours × rate`, recomputed live so the total tracks edits to either field. */
export const ProjectTotal = ({
  row,
  currencyCode,
}: {
  row: Row<GenerateBillListItem>;
  currencyCode: string;
}) => {
  const formContext = useFormContext();

  const hours = useWatch({ control: formContext.control, name: getBillFieldName(row, 'hours') });
  const rate = useWatch({
    control: formContext.control,
    name: getBillFieldName(row, 'effectiveRate'),
  });

  const calculatedTotal = roundToPrecision(
    parseFormNumber(hours) * parseFormNumber(rate),
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  );

  return (
    <div className="flex justify-between">
      <span>{formatTrimmedDecimal(calculatedTotal, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES)}</span>
      <span className="text-muted-foreground">{currencyCode}</span>
    </div>
  );
};

/** Overheads are flat amounts: the rate field IS the total (quantity is implicitly 1). */
export const OverheadTotal = ({ row }: { row: Row<GenerateBillListItem> }) => {
  const formContext = useFormContext();
  const rate = useWatch({
    control: formContext.control,
    name: getBillFieldName(row, 'effectiveRate'),
  });

  const total = roundToPrecision(parseFormNumber(rate), FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);
  const { code: currencyCode } = (row.original as unknown as GenerateBillMemberOverheadListItem)
    .currency;

  return (
    <div className="flex justify-between">
      <span>{formatTrimmedDecimal(total, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES)}</span>
      <span className="text-muted-foreground">{currencyCode}</span>
    </div>
  );
};
